import React, { useReducer, useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { gameStateManager } from '../../App';
import { AttemptUserLoginFlow, sessionUsername } from '../Core/LoginManager';
import { AttemptAdminPortalLoginFlow, isAdminUser } from '../Core/AdminLoginManager';

export default function PlaybackRouter()
{
  const [ , forceUpdate ] = useReducer( x => x + 1, 0 );
  const [ playbackInitialized, setPlaybackInitialized ] = useState( false );
  const location = useLocation();

  // Parse URL parameters
  const searchParams = new URLSearchParams( location.search );
  const bugReportId = searchParams.get( 'bugReportId' );
  const playedRoomId = searchParams.get( 'playedRoomId' );
  const skipToTile = parseInt( searchParams.get( 'skipToTile' ) || '0', 10 );
  const skipToFeature = parseInt( searchParams.get( 'skipToFeature' ) || '0', 10 );
  const speed = parseInt( searchParams.get( 'speed' ) || '2', 10 );
  const resolveAILogic = parseInt( searchParams.get( 'resolveAILogic' ) || '1', 10 ) == 1;

  // Register for game start events (always call this hook)
  useEffect( () =>
  {
    gameStateManager.onGameStartRegister( forceUpdate );

    return () =>
    {
      gameStateManager.onGameStartUnregister( forceUpdate );
    };
  }, [] );

  // Initialize playback when conditions are met (always call this hook)
  useEffect( () =>
  {
    if( ( bugReportId || playedRoomId ) && isAdminUser === true && !playbackInitialized )
    {
      const onServerErrorReceived = ( errorData ) =>
      {
        console.error( "PlaybackRouter - Server error received: ", errorData );
      };

      if( bugReportId )
      {
        console.log( "Starting bug report playback:", {
          bugReportId,
          skipToTile,
          skipToFeature,
          resolveAILogic,
          speed
        } );

        gameStateManager.createBugReportPlaybackSocket(
          bugReportId,
          () => { forceUpdate(); },
          onServerErrorReceived,
          skipToTile,
          skipToFeature,
          resolveAILogic,
          speed
        );
      }
      else if( playedRoomId )
      {
        console.log( "Starting played game playback:", {
          playedRoomId,
          skipToTile,
          skipToFeature,
          resolveAILogic,
          speed
        } );

        gameStateManager.createPlayedGamePlaybackSocket(
          playedRoomId,
          () => { forceUpdate(); },
          onServerErrorReceived,
          skipToTile,
          skipToFeature,
          resolveAILogic,
          speed
        );
      }

      setPlaybackInitialized( true );
    }
  }, [ bugReportId, playedRoomId, skipToTile, skipToFeature, speed, isAdminUser, playbackInitialized ] );

  // Check if user is authenticated and is admin
  if( !sessionUsername )
  {
    // User hasn't logged in yet
    AttemptUserLoginFlow( () =>
    {
      forceUpdate();
    }, null ); // No connection error handler for playback router

    return (
      <div style={ { textAlign: 'center', padding: '50px' } }>
        <h3>Authenticating...</h3>
        <p>Please wait while we verify your credentials.</p>
      </div>
    );
  }
  else if( isAdminUser === undefined )
  {
    // Check admin status
    AttemptAdminPortalLoginFlow( ( loginResponseData ) =>
    {
      console.log( "PlaybackRouter :: verifying admin user...", loginResponseData );
      forceUpdate();
    } );

    return (
      <div style={ { textAlign: 'center', padding: '50px' } }>
        <h3>Verifying Admin Access...</h3>
        <p>Checking your admin permissions.</p>
      </div>
    );
  }
  else if( !isAdminUser )
  {
    // Not an admin user
    return (
      <div style={ { textAlign: 'center', padding: '50px', color: '#dc3545' } }>
        <h3>Access Denied</h3>
        <p>You do not have permission to access bug report playback.</p>
        <p>Only administrators can view bug report playbacks.</p>
      </div>
    );
  }

  // Validate required parameters
  if( !bugReportId && !playedRoomId )
  {
    return (
      <div style={ { textAlign: 'center', padding: '50px', color: '#dc3545' } }>
        <h3>Invalid Playback URL</h3>
        <p>Neither bug report ID nor played room ID is provided in the URL.</p>
        <p>Please use a valid playback link from the admin panel.</p>
      </div>
    );
  }

  // Check if we're already connected to a room (playback started)
  if( gameStateManager.socketConnectedRoomId )
  {
    // Navigate to the connected room
    return (
      <Navigate to={ "/#" + gameStateManager.socketConnectedRoomId } replace={ true } />
    );
  }

  // Show loading state while initializing playback
  const playbackType = bugReportId ? "Bug Report" : "Played Game";
  const playbackId = bugReportId || playedRoomId;

  return (
    <div style={ { textAlign: 'center', padding: '50px' } }>
      <h3>Initializing { playbackType } Playback</h3>
      <p>Loading { playbackType.toLowerCase() }: <strong>{ playbackId }</strong></p>
      <div style={ { marginTop: '20px', color: '#6c757d' } }>
        <p>Skip to tile: { skipToTile }</p>
        <p>Skip to feature resolve: { skipToFeature }</p>
        <p>Playback speed: { speed } seconds</p>
      </div>
      <p style={ { marginTop: '30px', fontSize: '14px', color: '#6c757d' } }>
        Please wait while we prepare the playback...
      </p>
    </div>
  );
}
